<script setup lang="ts">
// AI モデル取得の同意画面。何をどこから取るか (URL / サイズ / ライセンス) を明示し、同意で保存する。
import { computed, ref } from 'vue'
import { useModal } from '@/composables/useModalStack'
import { useI18n } from '@/i18n'
import { useUiStore } from '@/stores/uiStore'
import { AI_MODELS, ORT_MODULE_URL, ORT_WASM_URL, ORT_WASM_BYTES, ORT_VERSION } from '@/ai/models'
import { aiConsent, giveAiConsent, revokeAiConsent, clearAiCache } from '@/ai/runtime'
import { runAiAction, type AiActionKind } from '@/ai/actions'
import { toast } from '@/composables/useToast'

const emit = defineEmits<{ close: [] }>()
useModal('ai-consent')
const { t } = useI18n()
const ui = useUiStore()
const pending = ui.dialogArg as AiActionKind | null
const modelId = ref(aiConsent.value?.modelId ?? AI_MODELS[0]!.id)
const model = computed(() => AI_MODELS.find(m => m.id === modelId.value)!)
const mb = (n: number) => `${(n / 1048576).toFixed(1)} MB`
function agree() {
  giveAiConsent(modelId.value)
  emit('close')
  if (pending) void runAiAction(pending).catch(e => toast.error(t('ai.failed', { reason: e instanceof Error ? e.message : String(e) })))
}
async function revoke() {
  revokeAiConsent()
  await clearAiCache()
  toast.info(t('ai.revoked'))
  emit('close')
}
</script>

<template>
  <div class="modal-backdrop" @pointerdown.self="emit('close')">
    <div class="modal consent" @keydown.esc="emit('close')">
      <h2>{{ t('ai.consent.title') }}</h2>
      <p>{{ t('ai.consent.body') }}</p>
      <table>
        <tr><th>{{ t('ai.consent.what') }}</th><th>{{ t('ai.consent.from') }}</th><th>{{ t('ai.consent.size') }}</th><th>{{ t('ai.consent.license') }}</th></tr>
        <tr><td>onnxruntime-web {{ ORT_VERSION }}</td><td class="mono url"><a :href="ORT_MODULE_URL" target="_blank" rel="noopener">cdn.jsdelivr.net</a></td><td class="mono">{{ mb(ORT_WASM_BYTES) }}</td><td>MIT</td></tr>
        <tr><td>{{ model.name }}</td><td class="mono url"><a :href="model.licenseUrl" target="_blank" rel="noopener">huggingface.co</a></td><td class="mono">{{ mb(model.bytes) }}</td><td>{{ model.license }}</td></tr>
      </table>
      <label class="field"><span>{{ t('ai.consent.model') }}</span>
        <select v-model="modelId">
          <option v-for="m in AI_MODELS" :key="m.id" :value="m.id">{{ m.name }} — {{ m.note }}</option>
        </select>
      </label>
      <ul class="muted small">
        <li>{{ t('ai.consent.note1') }}</li>
        <li>{{ t('ai.consent.note2') }}</li>
        <li>{{ t('ai.consent.note3') }}</li>
      </ul>
      <p class="mono tiny">{{ ORT_WASM_URL }}<br />{{ model.url }}</p>
      <div class="actions">
        <button v-if="aiConsent" @click="revoke">{{ t('ai.consent.revoke') }}</button>
        <span class="spacer" />
        <button @click="emit('close')">{{ t('dialog.cancel') }}</button>
        <button class="primary" @click="agree">{{ t('ai.consent.agree') }}</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.consent {
  max-width: 640px;
}
table {
  border-collapse: collapse;
  width: 100%;
  margin: 8px 0;
  font-size: 12px;
}
th,
td {
  text-align: left;
  padding: 4px 6px;
  border-bottom: 1px solid var(--line-weak);
}
th {
  color: var(--fg-2);
  font-weight: normal;
}
.small {
  font-size: 11px;
  padding-left: 18px;
}
.tiny {
  font-size: 10px;
  word-break: break-all;
  color: var(--fg-3);
}
.actions .spacer {
  flex: 1;
}
</style>
